/**
 * Aba "Lista de Desejos" da ficha da cliente, idêntica à do legado (`admin.html:2729-2745`,
 * `5391-5417`; linha 207 do inventário, e a trava de botão da linha 179).
 *
 * Três parágrafos fixos de orientação, a nota, o texto livre "Peças e prioridades" com
 * "Última atualização em ..." e o salvar com "Salvando...", "Salvo!" (some em 3 s) ou
 * "Erro ao salvar: " com a mensagem crua.
 *
 * Território da frente A7. A moldura importa esta aba pelo nome `AbaListaDesejos`.
 */

// Estado e efeito do React.
import { useEffect, useState } from 'react';
// As peças do Chassi.
import { Botao, CampoTexto } from '@complexity/ui';
// A leitura e a gravação da lista (A2).
import { lerListaDesejos, salvarListaDesejos } from '@studiolu/dados/areas/ficha';
// A fonte de dados do painel.
import { usePainel } from '../../../contexto.js';
// O parágrafo do status, peça comum (o texto em si vive com a página, ver `status-da-pagina.ts`).
import { LinhaDeStatus } from '../../../comum/status-temporario.js';
// O status que vive com a página, e não com a aba (achado da verificação da A7, linha 207).
import { criarStatusDaPagina, useStatusDaPagina } from './coloracao/status-da-pagina.js';
// O contrato da aba e o texto de "Última atualização em ...".
import type { FichaAberta, PropsDaAba } from '../comum-ficha/tipos.js';
import { textoUltimaAtualizacao } from '../comum-ficha/regras.js';
// O escopo estável, o recorte por papel e o aviso de ficha ausente, das abas da ficha (A3).
import { useEscopoEstavel } from './comum-a3/escopo.js';
import { useRecortePorPapel } from './comum-a3/papel.js';
import { AbaSemFicha } from './comum-a3/sem-ficha.js';

// O `#status-lista-desejos` do legado (`admin.html:2744`): um só, criado com o módulo e vivo com a página.
const statusListaDesejos = criarStatusDaPagina();

export function AbaListaDesejos({ ficha }: PropsDaAba) {
  // Sem a ficha aberta, a aba não consulta nada (ver `sem-ficha.tsx`).
  if (!ficha) return <AbaSemFicha aba="Lista de Desejos" />;
  return <ConteudoListaDesejos ficha={ficha} />;
}

function ConteudoListaDesejos({ ficha }: { readonly ficha: FichaAberta }) {
  const { dados } = usePainel();
  const { somenteLeitura } = useRecortePorPapel();
  const escopo = useEscopoEstavel(ficha);
  const [conteudo, setConteudo] = useState('');
  // O "Salvar" nasce travado e só destrava depois da leitura (linha 179, `admin.html:5977` e
  // `5404`): salvar antes gravaria o texto que estivesse no campo, de outra cliente.
  const [carregado, setCarregado] = useState(false);
  const status = useStatusDaPagina(statusListaDesejos);

  // A leitura ao abrir a aba, como o `carregarListaDesejos` do legado (`admin.html:5391-5405`).
  useEffect(() => {
    // Uma resposta que chega depois de a aba trocar de cliente não escreve na tela.
    let valendo = true;
    setCarregado(false);
    // O legado limpa o status antes de ler (`5393`). Só este ponto, a data lida e o salvar escrevem
    // nele: uma gravação ainda em curso que responder depois daqui escreve por cima, como no legado.
    statusListaDesejos.escrever('');
    void lerListaDesejos(dados, escopo).then((resultado) => {
      if (!valendo) return;
      // DEFEITO DO LEGADO REPRODUZIDO: falha na leitura só vai para o console, o campo fica vazio e
      // o "Salvar" destrava, então salvar gravaria o vazio por cima da lista (`5398-5404`).
      if (!resultado.ok) console.error('Erro ao carregar lista de desejos:', resultado.erro.mensagem);
      const linha = resultado.ok ? resultado.dados : null;
      setConteudo(linha?.conteudo || '');
      // "Última atualização em ..." só quando a linha tem a data (`5400-5402`).
      if (linha?.atualizado_em) statusListaDesejos.escrever(textoUltimaAtualizacao(linha.atualizado_em));
      setCarregado(true);
    });
    return () => {
      valendo = false;
    };
  }, [dados, escopo]);

  // "Salvar lista de desejos" (`admin.html:5407-5417`). A resposta escreve no parágrafo da página
  // mesmo com a aba fora da tela, como o legado escreve no nó do DOM (`5415`).
  const salvar = async () => {
    statusListaDesejos.escrever('Salvando...');
    // `atualizado_em` é o instante do clique, como o `new Date().toISOString()` do legado (`5412`).
    const resultado = await salvarListaDesejos(dados, escopo, { conteudo, atualizado_em: new Date().toISOString() });
    // Este ponto do legado mostra a mensagem CRUA depois do prefixo, e não a tradução.
    if (!resultado.ok) {
      statusListaDesejos.escrever('Erro ao salvar: ' + resultado.erro.mensagem);
      return;
    }
    statusListaDesejos.mostrar('Salvo!');
  };

  return (
    <section className="flex flex-col gap-cx-3" data-testid="aba-lista-desejos">
      <h3 className="text-cx-base font-semibold text-texto">Lista de Desejos</h3>
      {/* Os três parágrafos de orientação, o segundo inteiro em negrito (`admin.html:2731-2735`). */}
      <div className="flex flex-col gap-cx-3 text-cx-sm text-texto">
        <p>
          Ajude a cliente a criar uma lista de prioridades para futuras compras. Após finalizar cada etapa de
          revitalização de guarda-roupa, vocês precisam listar, conforme modelo abaixo, as peças que sentiram falta ou
          que acham necessárias incluir no armário.
        </p>
        <p>
          <strong>
            Depois de realizar a etapa de revitalização do armário da cliente, é hora de fazer a pré-pesquisa de peças
            necessárias, o que tornará a experiência em compras mais objetiva e prazerosa para ela. Afinal, não é nada
            prazeroso ficar rodando de loja em loja e não encontrar nada.
          </strong>
        </p>
        <p>
          Importante lembrar que você deve fazer essa pesquisa até, no máximo, três dias antes de ir com a sua cliente
          para as compras, pois as lojas, principalmente as de departamento, mudam constantemente a disposição de peças
          de um dia para o outro, principalmente em época de liquidação.
        </p>
      </div>
      <h4 className="mt-cx-4 text-cx-base font-medium text-texto">Lista de Desejos</h4>
      <p className="text-cx-sm text-texto-suave">
        Aqui você vai criar a lista de prioridades para futuras compras da sua cliente. Após cada etapa de revitalização
        de guarda-roupa, você vai listar, abaixo, as peças que você sentiu falta, e acredita que são necessárias para a
        sua cliente investir.
      </p>
      {/* Consulta: campo desabilitado (`admin.html:3394`). */}
      <CampoTexto
        rotulo="Peças e prioridades"
        linhas={10}
        placeholder="Liste aqui as peças que a cliente sentiu falta e que você julga necessárias incluir no armário dela..."
        value={conteudo}
        disabled={somenteLeitura}
        onChange={(evento) => setConteudo(evento.target.value)}
      />
      {/* Consulta não vê botão de ação (`admin.html:3395`). O botão NÃO trava durante o envio, como
          no legado (`5407-5417`). */}
      {!somenteLeitura && (
        <div>
          <Botao disabled={!carregado} onClick={() => void salvar()}>
            Salvar lista de desejos
          </Botao>
        </div>
      )}
      <LinhaDeStatus texto={status} testid="status-lista-desejos" />
    </section>
  );
}
